import { clsx } from 'clsx';
import { MapPin } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { buttonClasses } from '@/components/ui/button-classes';
import { Placeholder } from '@/components/ui/Placeholder';
import { useConsentStore } from '@/lib/consent/store';
import { placeUrl } from '@/lib/site/map';
import { content } from './content';

export interface MapFrameProps {
  /** An https embed address (`contact.mapEmbedUrl`), already checked; `null` = none yet. */
  embedUrl: string | null;
  address: string | null;
  /** `page`: the large map of the contact page. `footer`: the compact one under the tagline, on every page. */
  variant?: 'page' | 'footer';
}

/**
 * The map of the works. Loading Google's embed contacts Google (the visitor's address, Google's own cookies),
 * so — like the analytics — it does not happen on its own: the map is embedded at once only for a visitor who
 * accepted cookies; for everyone else it is a framed panel with the pin as a link, a note, and one button that
 * loads the map in place (D-316, D-318). Nothing about it reaches Google before that. Its styles are the
 * shell's (`styles/components/footer.css`), because the footer's panel is part of the pre-rendered page.
 */
export function MapFrame({ embedUrl, address, variant = 'page' }: MapFrameProps) {
  const copy = content;
  const accepted = useConsentStore((state) => state.decision === 'accepted');
  const [requested, setRequested] = useState(false);
  const place = placeUrl(address, embedUrl);
  const frame = clsx('map-frame', `map-frame--${variant}`);

  if (embedUrl === null) {
    // the footer shows nothing rather than a marked token on every page
    if (variant === 'footer') return null;
    return (
      <div className={frame} aria-label={copy.map} role="group">
        <p className="map-frame__empty">
          <Placeholder token="MAP_EMBED_URL" />
          <span>{copy.placeholder}</span>
        </p>
      </div>
    );
  }

  return (
    <div className={frame} aria-label={copy.map} role="group">
      {accepted || requested ? (
        <iframe
          title={copy.map}
          src={embedUrl}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      ) : (
        <div className="map-frame__facade">
          <MapPin
            size={variant === 'footer' ? 22 : 28}
            aria-hidden="true"
            className="map-frame__pin"
          />
          <p>{copy.note}</p>
          <div className="map-frame__actions">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setRequested(true);
              }}
            >
              {copy.show}
            </Button>
            {place ? (
              <a
                className={buttonClasses({ variant: 'ghost', size: 'sm' })}
                href={place}
                target="_blank"
                rel="noopener noreferrer"
              >
                {copy.open}
                <span className="sr-only"> {copy.opensInNewTab}</span>
              </a>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
